/**
 * Utility functions for data validation
 */

/**
 * Check if the date is valid. The date must be in the format "mm/dd/yyyy".
 * @param dateStr The date string to validate. Must be in the format "mm/dd/yyyy".
 */
export function isValidDate(dateStr?: string | null): boolean {
    // It is not enough to call "Date.parse(dateStr)" or "new Date(dateStr)" because
    // these will not catch the cases like "02/31/1999".
    if (!dateStr) {
        return false;
    }

    // This method assumes that the date is formatted in an American format "mm/dd/yyyy". Support
    // for other locales should be added separately.
    const dateParts = dateStr.split('/');
    if (dateParts.length !== 3) {
        return false;
    }

    const month = parseInt(dateParts[0], 10);
    const day = parseInt(dateParts[1], 10);
    const year = parseInt(dateParts[2], 10);

    // Any constraints like "date of birth should be plausible" or "ID issue date should be plausible"
    // should be added separately.
    if (year < 0 || year > 9999) {
        return false;
    }

    // Check if the month is in the valid range
    if (month < 1 || month > 12) {
        return false;
    }

    // Check if the day is in the valid range
    if (day < 1 || day > 31) {
        return false;
    }

    // Check if the day is valid for the month
    if (day > 30 && (month === 4 || month === 6 || month === 9 || month === 11)) {
        return false;
    }

    // Check whether this is a valid February date.
    if (day > 29 && month === 2) {
        return false;
    }

    if (day > 28 && month === 2 && !isLeapYear(year)) {
        return false;
    }

    return true;
}

function isLeapYear(year: number): boolean {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/**
 * Checks if a date indicates the person is at least the minimum age (default 18)
 * @param dateStr The date of birth
 * @param minAge The minimum age required (default: 18)
 */
export const isOldEnough = (dateStr: string, minAge: number = 18): boolean => {
    const dobDate = new Date(dateStr);

    const today = new Date();
    const minDate = new Date(today.getFullYear() - minAge, today.getMonth(), today.getDate());

    return dobDate <= minDate;
};
